import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useMutation, useQuery } from "@tanstack/react-query";

import { api } from "@/lib/api";

interface ProductRef {
  slug: string;
  display_name: string;
}

export default function NewTicketPage() {
  const navigate = useNavigate();
  const products = useQuery({
    queryKey: ["portal-products"],
    queryFn: () => api<{ items: ProductRef[] }>("/portal/products"),
  });

  const [productSlug, setProductSlug] = useState("");
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [severity, setSeverity] = useState("");

  const m = useMutation({
    mutationFn: () =>
      api<{ public_id: string }>("/portal/tickets", {
        method: "POST",
        body: JSON.stringify({
          product_slug: productSlug,
          title: title.trim(),
          body_markdown: body.trim(),
          severity: severity || null,
        }),
      }),
    onSuccess: (data) => navigate(`/tickets/${data.public_id}`),
  });

  return (
    <div className="space-y-4">
      <div className="text-xs text-slate-500">
        <Link to="/my-tickets" className="underline">← My tickets</Link>
      </div>
      <h1 className="text-2xl font-semibold">New ticket</h1>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          m.mutate();
        }}
        className="space-y-4 rounded-md border border-slate-200 bg-white p-4"
      >
        <label className="block text-sm">
          Product
          <select
            value={productSlug}
            onChange={(e) => setProductSlug(e.target.value)}
            required
            className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 text-sm"
          >
            <option value="">Pick one…</option>
            {(products.data?.items ?? []).map((p) => (
              <option key={p.slug} value={p.slug}>{p.display_name}</option>
            ))}
          </select>
        </label>
        <label className="block text-sm">
          Title
          <input
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            required
            maxLength={200}
            className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 text-sm"
          />
        </label>
        <label className="block text-sm">
          What's going on?
          <textarea
            value={body}
            onChange={(e) => setBody(e.target.value)}
            required
            rows={8}
            maxLength={20_000}
            placeholder="What were you trying to do? What did you see instead? Steps to reproduce help us a lot."
            className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 text-sm"
          />
        </label>
        <label className="block text-sm">
          Severity (optional)
          <select
            value={severity}
            onChange={(e) => setSeverity(e.target.value)}
            className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 text-sm"
          >
            <option value="">— not sure —</option>
            <option value="low">Low — minor inconvenience</option>
            <option value="medium">Medium — workaround exists</option>
            <option value="high">High — blocking my work</option>
            <option value="critical">Critical — outage, data loss</option>
          </select>
        </label>

        <button
          type="submit"
          disabled={!productSlug || !title.trim() || !body.trim() || m.isPending}
          className="w-full rounded-md bg-blue-600 px-3 py-2 text-sm font-medium text-white hover:bg-blue-500 disabled:opacity-50"
        >
          {m.isPending ? "Submitting…" : "Submit ticket"}
        </button>
        {m.isError ? (
          <div className="text-xs text-red-600">{(m.error as Error).message}</div>
        ) : null}
      </form>
    </div>
  );
}
